import React from "react";
import { Link } from "react-router-dom";
import type { IconType } from "react-icons";
import { IoChevronForward } from "react-icons/io5";
import { PhotoCarousel } from "./PhotoCarousel";
import { CLINICAL_CTSI_IMAGES } from "../../constants/clinicalCtsiImages";
import { TEASER_FIGURES } from "../../data/research";
import { useReveal } from "../../hooks/useReveal";
import styles from "./ProjectSection.module.css";

export const asIcon = (icon: IconType): React.ElementType =>
  icon as React.ElementType;


const PARAGRAPHS: readonly string[] = [
  "Stroke often leaves lasting upper-limb disability, and gains from formal therapy can fade without ongoing practice at home.",
  "mRehab grew out of research on affordable, portable tools: pairing smartphone sensors with optional 3D-printed accessories to capture movement, surface useful feedback, and help people stay engaged in upper-limb rehabilitation between visits. What used to be paper handouts becomes something measurable and motivating.",
];

/*
 * These photos are from CTSI community sessions where the project was
 * presented to survivors, families and clinicians. The alt text describes
 * what each frame actually shows.
 */
const SLIDE_ALTS: readonly string[] = [
  "Attendees seated at tables during a CTSI community session on mRehab",
  "A researcher presenting the mRehab project to a seated audience",
  "A slide explaining the 3D-printed practice tools shown to the room",
  "Attendees watching the closing slide of the mRehab session",
  "A wide view of the room during a CTSI community session",
];

export { PhotoCarousel } from "./PhotoCarousel";

export function ProjectSection() {
  const ref = useReveal<HTMLElement>();

  return (
    <section
      className={`${styles.band} ui-reveal`}
      id="research"
      aria-labelledby="research-title"
      ref={ref}
    >
      <div className="ui-band-inner">
        <PhotoCarousel
          images={CLINICAL_CTSI_IMAGES}
          alts={SLIDE_ALTS}
          label="Photos from CTSI community sessions"
          fallbackAltPrefix="CTSI community session photo"
          className={styles.carousel}
          slideClassName={styles.wideMedia}
        />

        <div className={styles.text}>
          <div className={styles.textLead}>
            <h2 id="research-title" className={styles.title}>
              Built on research. Tested by survivors.
            </h2>
            <p className={styles.lead}>{PARAGRAPHS[0]}</p>
          </div>
          <div className={styles.prose}>
            {PARAGRAPHS.slice(1).map((text) => (
              <p key={text}>{text}</p>
            ))}
          </div>
        </div>
        <dl className="ui-fact-strip" aria-label="Research results at a glance">
          {TEASER_FIGURES.map((fact) => (
            <div className="ui-fact" key={fact.label}>
              <dt className="ui-fact-label">{fact.label}</dt>
              <dd className="ui-fact-value">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <Link to="/research" className="ui-link research-text-link">
          Read the research <IoChevronForward aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}
